import React from 'react';
import { Save, Loader2, Server, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

/**
 * Custom SMTP configuration form — pure presentational. Parent owns
 * every field's state + the submit handler. `ready` toggles the green
 * "Configured" pill + flips the password placeholder so admins know
 * leaving it blank keeps the existing credential.
 */
export function SmtpForm({
  form,         // { host, port, username, password, showPwd, fromEmail, fromName, useTls }
  set,          // (key, value) => void
  ready,
  saving,
  onSubmit,
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="smtp-form"
      className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-5"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
          <Server className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <div className="text-white font-semibold flex items-center gap-2">
            Custom SMTP
            {ready && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                <CheckCircle2 className="inline w-2.5 h-2.5 mr-1" /> Configured
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400">
            Any provider that accepts SMTP/STARTTLS — Mailgun, Postmark, Brevo, Office365, etc.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Host">
          <Input value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="smtp.mailgun.org" data-testid="smtp-host"
            className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" />
        </Field>
        <Field label="Port">
          <Input type="number" value={form.port} onChange={(e) => set('port', e.target.value)} placeholder="587" data-testid="smtp-port"
            className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" />
        </Field>
        <Field label="Username">
          <Input value={form.username} onChange={(e) => set('username', e.target.value)} placeholder="api or your username" data-testid="smtp-username"
            className="bg-slate-800/50 border-slate-700 text-white text-sm" />
        </Field>
        <Field label="Password">
          <div className="relative">
            <Input
              type={form.showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              placeholder={ready ? '•••••••• (leave blank to keep)' : 'SMTP password / API token'}
              data-testid="smtp-password"
              className="bg-slate-800/50 border-slate-700 text-white text-sm pr-10"
            />
            <button
              type="button"
              onClick={() => set('showPwd', !form.showPwd)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              tabIndex={-1}
            >
              {form.showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <Field label="From email">
          <Input value={form.fromEmail} onChange={(e) => set('fromEmail', e.target.value)} placeholder="noreply@yourdomain.com" data-testid="smtp-from-email"
            className="bg-slate-800/50 border-slate-700 text-white text-sm" />
        </Field>
        <Field label="From name">
          <Input value={form.fromName} onChange={(e) => set('fromName', e.target.value)} placeholder="Your Brand" data-testid="smtp-from-name"
            className="bg-slate-800/50 border-slate-700 text-white text-sm" />
        </Field>
      </div>

      <div className="flex items-center gap-3">
        <Switch id="smtp-tls" checked={form.useTls} onCheckedChange={(v) => set('useTls', v)} data-testid="smtp-use-tls" />
        <Label htmlFor="smtp-tls" className="text-slate-300 text-xs">
          Use STARTTLS (recommended for ports 587 / 25 — port 465 implies SSL automatically)
        </Label>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} data-testid="smtp-save"
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save SMTP configuration</>}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

export default SmtpForm;
